/**
 * Leumit session helpers — JWT token persistence and sessionId derivation.
 * Never log the raw JWT.
 */

const LEUMIT_TOKEN_STORAGE_KEY = 'leumit_jwt_token';
const SESSION_ID_HEX_LENGTH = 10;

const tryLocalStorage = (): boolean => {
  try {
    const testKey = '__leumit_storage_test__';
    localStorage.setItem(testKey, '1');
    localStorage.removeItem(testKey);
    return true;
  } catch {
    return false;
  }
};

/**
 * Persists the Leumit JWT token for later forwarding in the SessionEndResponse.
 */
export const setLeumitToken = (token: string): void => {
  if (!token || !tryLocalStorage()) {
    return;
  }

  try {
    localStorage.setItem(LEUMIT_TOKEN_STORAGE_KEY, token);
  } catch {
    console.warn('[leumitSession] Failed to persist token');
  }
};

/**
 * Returns the persisted Leumit JWT token, or null if missing/unavailable.
 */
export const getLeumitToken = (): string | null => {
  if (!tryLocalStorage()) {
    return null;
  }

  try {
    return localStorage.getItem(LEUMIT_TOKEN_STORAGE_KEY);
  } catch {
    return null;
  }
};

/**
 * Removes the persisted Leumit JWT token.
 */
export const clearLeumitToken = (): void => {
  if (!tryLocalStorage()) {
    return;
  }

  try {
    localStorage.removeItem(LEUMIT_TOKEN_STORAGE_KEY);
  } catch {
    // Non-critical cleanup failure
  }
};

/**
 * Derives an internal sessionId from a JWT token:
 * SHA-256(token) -> hex -> first 10 chars.
 * Satisfies sessionId validation (a-z A-Z 0-9 _).
 */
export const deriveSessionIdFromToken = async (token: string): Promise<string> => {
  if (!token) {
    throw new Error('Cannot derive sessionId from empty token');
  }

  if (typeof crypto === 'undefined' || !crypto.subtle) {
    throw new Error('Web Crypto API (crypto.subtle) is unavailable');
  }

  const encoder = new TextEncoder();
  const data = encoder.encode(token);
  const hashBuffer = await crypto.subtle.digest('SHA-256', data);
  const hashArray = Array.from(new Uint8Array(hashBuffer));
  const hexHash = hashArray.map((byte) => byte.toString(16).padStart(2, '0')).join('');

  return hexHash.slice(0, SESSION_ID_HEX_LENGTH);
};
